import { createEffect, createMemo, createSignal, For, Show, untrack } from 'solid-js';
import Modal from './Modal';
import Text from './Text';
import { useTranslation } from '../hooks/useTranslation';
import { actionLabelKey, roleDescriptionKey, roleNameKey, SENSITIVE_ACTIONS, type RoleInfo } from '../services/resourceActions';
import styles from './GrantAccessModal.module.css';

export interface GrantAccessValue {
    username?: string;
    tenantId?: string;
    roleId: string;
    expiresAt?: string;
    clearExpiry: boolean;
}

interface GrantAccessModalProps {
    open: boolean;
    mode: 'user' | 'tenant' | 'edit';
    roles: RoleInfo[];
    tenants: { label: string; value: string }[];
    existing?: { principalName: string; roleId: string; expiresAt: string | null };
    busy: boolean;
    onClose: () => void;
    onSubmit: (value: GrantAccessValue) => void;
}

// Granting access is a consequential, hard-to-review decision, so it gets a full surface rather
// than showDialog's field list: the role's every capability is spelled out in the picker, and the
// ones that move money, reach the Minecraft account or hand on the grant power are called out.
export default function GrantAccessModal(props: GrantAccessModalProps) {
    const { t } = useTranslation();

    const [username, setUsername] = createSignal('');
    const [tenantId, setTenantId] = createSignal('');
    const [roleId, setRoleId] = createSignal('');
    const [expiresAt, setExpiresAt] = createSignal('');

    createEffect(() => {
        if (!props.open) return;
        untrack(() => {
            setUsername('');
            setTenantId(props.tenants[0]?.value ?? '');
            setRoleId(props.existing?.roleId ?? '');
            setExpiresAt(props.existing?.expiresAt ? props.existing.expiresAt.slice(0, 10) : '');
        });
    });

    const roleLabel = (r: RoleInfo) => {
        const key = roleNameKey(r.id);
        return key ? t(key) : r.name;
    };

    const roleDescription = (r: RoleInfo) => {
        const key = roleDescriptionKey(r.id);
        return key ? t(key) : r.description;
    };

    const selected = createMemo(() => props.roles.find(r => r.id === roleId()) ?? null);
    const sensitiveCount = (r: RoleInfo) => r.actions.filter(a => SENSITIVE_ACTIONS.has(a)).length;

    const principalName = () => props.mode === 'edit'
        ? props.existing?.principalName ?? ''
        : props.mode === 'tenant'
            ? props.tenants.find(x => x.value === tenantId())?.label ?? ''
            : username().trim();

    const valid = () => !!roleId()
        && (props.mode !== 'user' || username().trim().length > 0)
        && (props.mode !== 'tenant' || !!tenantId());

    const submit = () => {
        if (!valid() || props.busy) return;
        props.onSubmit({
            username: props.mode === 'user' ? username().trim() : undefined,
            tenantId: props.mode === 'tenant' ? tenantId() : undefined,
            roleId: roleId(),
            expiresAt: expiresAt() || undefined,
            clearExpiry: !expiresAt() && !!props.existing?.expiresAt,
        });
    };

    const title = () => props.mode === 'edit'
        ? t('ACCESS_EDIT_FOR', props.existing?.principalName ?? '')
        : props.mode === 'tenant'
            ? t('ACCESS_GRANT_TITLE_TEAM')
            : t('ACCESS_GRANT_TITLE_USER');

    return (
        <Modal open={props.open} onClose={props.onClose} maxWidth="46rem"
            title={title()} subtitle={<Text val="ACCESS_GRANT_SUBTITLE" />}
            leading={<i class="fa-solid fa-shield-halved" />}>
            <form class="v" style={{ gap: 'var(--spacing-lg)' }}
                onSubmit={(e) => { e.preventDefault(); submit(); }}>

                <Show when={props.mode === 'user'}>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span class={styles.fieldLabel}><Text val="USERNAME" /></span>
                        <input type="text" value={username()} autocomplete="off"
                            onInput={(e) => setUsername(e.currentTarget.value)} />
                    </label>
                </Show>

                <Show when={props.mode === 'tenant'}>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span class={styles.fieldLabel}><Text val="ACCESS_TENANT" /></span>
                        <select value={tenantId()} onChange={(e) => setTenantId(e.currentTarget.value)}>
                            <For each={props.tenants}>{(x) => <option value={x.value}>{x.label}</option>}</For>
                        </select>
                    </label>
                </Show>

                <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                    <span class={styles.fieldLabel}><Text val="ACCESS_ROLE" /></span>
                    <div class={styles.roleList} role="radiogroup" aria-label={t('ACCESS_ROLE')}>
                        <For each={props.roles}>
                            {(r) => (
                                <button type="button" role="radio" aria-checked={roleId() === r.id}
                                    class={styles.roleCard} classList={{ [styles.roleCardSelected]: roleId() === r.id }}
                                    onClick={() => setRoleId(r.id)}>
                                    <span class={styles.roleHead}>
                                        <i class={roleId() === r.id ? 'fa-solid fa-circle-dot' : 'fa-regular fa-circle'} />
                                        <span class={styles.roleName}>{roleLabel(r)}</span>
                                        <span class={styles.roleCount}>
                                            {t('ACCESS_ROLE_ACTION_COUNT', String(r.actions.length))}
                                        </span>
                                        <Show when={sensitiveCount(r) > 0}>
                                            <span class={styles.sensitiveTag}
                                                title={t('ACCESS_SENSITIVE_WARNING', String(sensitiveCount(r)))}>
                                                <i class="fa-solid fa-triangle-exclamation" /> {sensitiveCount(r)}
                                            </span>
                                        </Show>
                                    </span>
                                    <span class={styles.roleDesc}>{roleDescription(r)}</span>
                                </button>
                            )}
                        </For>
                    </div>
                </div>

                <div class={styles.caps}>
                    <Show when={selected()} fallback={
                        <p class={styles.capsEmpty}>
                            <i class="fa-solid fa-hand-pointer" /> <Text val="ACCESS_NO_ROLE_SELECTED" />
                        </p>
                    }>
                        <div class={styles.capsHead}>
                            <Text val="ACCESS_ROLE_CAPABILITIES" />
                            <Show when={sensitiveCount(selected()!) > 0}>
                                <span class={styles.capsWarn}>
                                    <i class="fa-solid fa-triangle-exclamation" />
                                    {' '}{t('ACCESS_SENSITIVE_WARNING', String(sensitiveCount(selected()!)))}
                                </span>
                            </Show>
                        </div>
                        <ul class={styles.capList}>
                            <For each={selected()!.actions}>
                                {(a) => (
                                    <li class={styles.capRow} classList={{ [styles.capSensitive]: SENSITIVE_ACTIONS.has(a) }}
                                        title={a}>
                                        <i class={SENSITIVE_ACTIONS.has(a)
                                            ? 'fa-solid fa-triangle-exclamation'
                                            : 'fa-solid fa-check'} />
                                        <span>{t(actionLabelKey(a))}</span>
                                        <Show when={SENSITIVE_ACTIONS.has(a)}>
                                            <span class={styles.sensitiveTag}><Text val="ACCESS_SENSITIVE" /></span>
                                        </Show>
                                    </li>
                                )}
                            </For>
                        </ul>
                    </Show>
                </div>

                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span class={styles.fieldLabel}><Text val="ACCESS_EXPIRES" /></span>
                    <input type="date" value={expiresAt()}
                        onInput={(e) => setExpiresAt(e.currentTarget.value)} />
                    <span class={styles.hint}><Text val="ACCESS_EXPIRES_HINT" /></span>
                </label>

                <Show when={selected() && principalName()}>
                    <p class={styles.summary}>
                        <i class="fa-solid fa-circle-info" />
                        {' '}{t('ACCESS_GRANT_SUMMARY', principalName(), String(selected()!.actions.length))}
                    </p>
                </Show>

                <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                    <button type="button" class="btn btn-secondary" onClick={props.onClose} disabled={props.busy}>
                        <Text val="CANCEL" />
                    </button>
                    <button type="submit" class="btn btn-primary" disabled={!valid() || props.busy}>
                        <Show when={props.busy}><i class="fa-solid fa-spinner fa-spin" />{' '}</Show>
                        <Text val={props.mode === 'edit' ? 'ACCESS_SAVE_CHANGES' : 'ACCESS_GRANT_CONFIRM'} />
                    </button>
                </div>
            </form>
        </Modal>
    );
}
